//Built-in libraries
import type { ColumnDef } from '@tanstack/react-table';

//Third party libraries
import { useLocation, useNavigate } from 'react-router';
//Custom components
import { CurrencyFormatDisplay } from '@/components/UI/NumberFormatDisplay/CurrencyFormatDisplay';
import Table from '@/components/UI/Table';
import { useApplicationsListContext } from '@/features/ApplicationForm/Components/ApplicationsList/ApplicationsListProvider';
//Services
import { convertEnumToText } from '@/utils';
import { BTPPFiltersForm } from './BTPPFiltersForm';

//STP Libraries

import dayjs from 'dayjs';
import { DATE_FORMAT } from '@/constants';
//Assets and Miscellaneous
import Styles from './styles.module.css';

//Component Begin

export const BTPPApplicationsList = () => {
  const { data, tablePagination, showFilterModal, toggleFilterModal, applyFiltersHandler } = useApplicationsListContext();

  const navigate = useNavigate();
  const location = useLocation();

  const tableColumns: ColumnDef<any>[] = [
    {
      id: 'proposerName',
      header: 'Proposer Name',
      accessorKey: 'applicationData',
      size: 250,
      cell: (props) => {
        const { getValue } = props;
        const applicationData = getValue() as any;
        const applicationInformation = applicationData?.applicantInformation;
        return (
          <div className='text-sm text-neutralColor-medium flex flex-col max-w-md'>
            <span className='truncate'>{applicationInformation?.entityName}</span>
            <span className='text-xs text-neutralColor-normal'>{applicationInformation.contactPersonEmail}</span>
          </div>
        );
      },
    },
    {
      id: 'proposedProperty',
      header: 'Proposed Property',
      accessorKey: 'applicationData',
      size: 250,
      cell: (props) => {
        const { getValue } = props;
        const applicationData = getValue() as any;
        const projectDetails = applicationData?.projectDetails;
        return (
          <div className='flex flex-col gap-1'>
            <span>{projectDetails.streetAddress}</span>
            <span className='text-xs text-neutralColor-normal'>
              {projectDetails.city}, {projectDetails.state}
            </span>
            <span className='text-xs text-neutralColor-normal'>{projectDetails.zipCode}</span>
          </div>
        );
      },
    },
    {
      id: 'developmentCost',
      header: 'Development Cost',
      accessorKey: 'applicationData',
      size: 200,
      cell: (props) => {
        const { getValue } = props;
        const applicationData = getValue() as any;
        const projectDetails = applicationData?.projectDetails;
        return (
          <div className='flex flex-col gap-1'>
            <CurrencyFormatDisplay value={projectDetails?.developmentCostEstimate} prefix='$' />
          </div>
        );
      },
    },
    {
      id: 'acquisitionType',
      header: 'Acquisition Type',
      accessorKey: 'applicationData.projectDetails.acquisitionType',
      size: 200,
    },
    {
      id: 'constructionType',
      header: 'Construction Type',
      accessorKey: 'applicationData.projectDetails.constructionType',
      size: 200,
    },
    {
      id: 'dealType',
      header: 'Deal Type',
      accessorKey: 'applicationData.projectDetails.dealType',
      size: 200,
    },
    {
      id: 'status',
      header: 'Status',
      accessorKey: 'status',
      size: 250,
      cell: (props) => {
        const { getValue } = props;
        return (
          <div className='flex items-center w-full capitalize'>
            <span className={`${Styles[getValue() as string]}`}>{convertEnumToText(getValue() as string)}</span>
          </div>
        );
      },
    },
    {
      id: 'createdAt',
      header: 'Submitted',
      accessorKey: 'createdAt',
      size: 250,
      cell: (props) => {
        const { getValue } = props;
        return <div className='flex items-center w-full capitalize'>{dayjs(getValue() as string).format(DATE_FORMAT)}</div>;
      },
    },
  ];

  return (
    <>
      <Table
        cols={tableColumns}
        tableData={data?.data}
        onRowClick={(row: any) => navigate(`${location.pathname}/${row.id}`)}
        pagination={tablePagination}
      />

      {showFilterModal && <BTPPFiltersForm handleModal={toggleFilterModal} handleApplyFilter={applyFiltersHandler} />}
    </>
  );
};
